import React, { useEffect, useState, useCallback } from "react";
import { Plus, ShieldCheck, Store } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import { PageHeader } from "@/components/PageHeader";
import { BlockSkeleton } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const empty = { email: "", password: "", name: "", role: "manager", allowed_brand_ids: [] };

export default function UsersPage() {
  const { nonCorpBrands, brands } = useFilters();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const brandName = Object.fromEntries(brands.map((b) => [b.id, b.name]));

  const load = useCallback(() => {
    setLoading(true);
    api.get("/users").then((res) => setUsers(res.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, is_active: !u.is_active } : x)); toast.success(u.is_active ? "User deactivated" : "User activated"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleBrand = (id) => setForm((f) => ({ ...f, allowed_brand_ids: f.allowed_brand_ids.includes(id) ? f.allowed_brand_ids.filter((x) => x !== id) : [...f.allowed_brand_ids, id] }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.role === "manager" && form.allowed_brand_ids.length === 0) { toast.error("Assign at least one brand for a manager"); return; }
    setSaving(true);
    try {
      await api.post("/users", { ...form, allowed_brand_ids: form.role === "corporate" ? [] : form.allowed_brand_ids });
      toast.success("User created");
      setDialogOpen(false); setForm(empty); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      <PageHeader title="Users & Access" subtitle="Manage staff accounts and brand permissions.">
        <Button data-testid="add-user-button" onClick={() => { setForm(empty); setDialogOpen(true); }} className="h-9 gap-2 rounded-full bg-[#18344A] text-white hover:bg-[#18344A]/90">
          <Plus className="h-4 w-4" /> Add user
        </Button>
      </PageHeader>

      <Card className="overflow-hidden rounded-[14px] border-[var(--pb-border)] bg-white shadow-[var(--pb-shadow-sm)]">
        {loading ? (
          <div className="p-4"><BlockSkeleton className="h-96" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow className="bg-[rgba(24,52,74,0.04)]">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Brands</TableHead>
                  <TableHead>Active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="hover:bg-[rgba(183,165,142,0.1)]">
                    <TableCell className="font-medium text-[#30383C]">{u.name}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-full text-[11px] ${u.role === "corporate" ? "border-[rgba(24,52,74,0.25)] bg-[rgba(24,52,74,0.08)] text-[#18344A]" : "border-[rgba(82,110,126,0.3)] bg-[rgba(82,110,126,0.12)] text-[#526E7E]"}`}>
                        {u.role === "corporate" ? <ShieldCheck className="mr-1 h-3 w-3" /> : <Store className="mr-1 h-3 w-3" />}
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[280px] text-xs text-[rgba(48,56,60,0.7)]">
                      {u.role === "corporate" ? "All brands" : (u.allowed_brand_ids || []).map((id) => brandName[id]).filter(Boolean).join(", ") || "\u2014"}
                    </TableCell>
                    <TableCell>
                      <Switch data-testid={`user-active-${u.id}`} checked={u.is_active} onCheckedChange={() => toggleActive(u)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-[#18344A]">Add user</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="user-form">
            <div className="space-y-1.5"><Label>Full name</Label><Input required data-testid="user-name" value={form.name} onChange={(e) => set("name")(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" required data-testid="user-email" value={form.email} onChange={(e) => set("email")(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Temporary password</Label><Input type="text" required data-testid="user-password" value={form.password} onChange={(e) => set("password")(e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={set("role")}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="corporate">Corporate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "manager" && (
              <div className="space-y-2">
                <Label>Assigned brands</Label>
                <div className="grid max-h-44 grid-cols-1 gap-2 overflow-y-auto rounded-[10px] border border-[var(--pb-border)] p-3">
                  {nonCorpBrands.map((b) => (
                    <label key={b.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.allowed_brand_ids.includes(b.id)} onCheckedChange={() => toggleBrand(b.id)} data-testid={`user-brand-${b.id}`} />
                      {b.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="user-form-submit" className="bg-[#18344A] text-white hover:bg-[#18344A]/90">{saving ? "Saving\u2026" : "Create user"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
